import {
  Button,
  Chip,
  DateColumn,
  Delimiter,
  FileManager,
  Modal,
  PageModal,
  Typography,
} from '@otp/ui';
import { useTranslation } from 'react-i18next';
import { useQuestion } from 'src/hooks/query/use-question';
import { getFullName } from 'src/utils/user-data';
import { PinList } from '@otp/icons';
import { DocumentNode } from '@apollo/client';
import { ImportanceField } from '../table/columns/importance';
import { CategoryField } from '../table/columns/category';
import { StatusField } from '../table/columns/status';
import { mapFileAssetsToForm } from 'src/components/common/resources/form/utils';
import { useState } from 'react';
import { WttpQuestionsDocument } from 'src/graphql/generated/graphql';
import { QuestionForm } from '../../form';
import { WttpQuestionAction } from '../table/columns/actions/actions-modals';

type EntityDetailsProps = {
  entityId: string;
  onClose: () => void;
  onActionFinished?: (action: WttpQuestionAction) => void;
  refetchQueries?: DocumentNode[];
};

export const EntityDetails = ({
  entityId,
  onClose,
  onActionFinished,
}: EntityDetailsProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'wttp.question.details',
  });
  const editFormTranslation = useTranslation(undefined, {
    keyPrefix: 'wttp.question.form',
  });
  const [displayEditForm, setDisplayEditForm] = useState(false);
  const { entity } = useQuestion({ id: entityId });

  if (!entity) {
    return null;
  }

  const files = mapFileAssetsToForm(entity.fileAssets);
  const onEditFinished = () => {
    onActionFinished?.(WttpQuestionAction.Edit);
    onCloseEditForm();
  };
  const onCloseEditForm = () => setDisplayEditForm(false);
  return (
    <PageModal
      open={true}
      title={
        (
          <div>
            <Typography
              variant="subtitle1"
              className="font-bold flex flex-row justify-between items-center gap-2"
            >
              <PinList />
              {t('question-details-title')}
            </Typography>
          </div>
        ) as any
      }
      onClose={onClose}
    >
      <div className="w-full min-w-small md:min-w-medium">
        <div>
          <div className="mb-2">
            <div className="flex flex-row justify-between items-center gap-2">
              <Typography variant="subtitle1" className="font-bold">
                {t('question-title')}
              </Typography>
              <Button
                className="px-1 min-w-0"
                onClick={() => setDisplayEditForm(true)}
              >
                {t('edit')}
              </Button>
            </div>
            <Typography variant="subtitle2">
              <div dangerouslySetInnerHTML={{ __html: entity.title }} />
            </Typography>
          </div>
          {entity.description && (
            <div className="my-2">
              <Typography variant="subtitle1" className="font-bold">
                {t('question-description')}
              </Typography>
              <Typography variant="subtitle2">
                <div dangerouslySetInnerHTML={{ __html: entity.description }} />
              </Typography>
            </div>
          )}

          <div>
            <div className="flex flex-row justify-between items-center gap-2">
              <Typography
                variant="subtitle1"
                className="mb-2 font-bold flex flex-row gap-2"
              >
                {t('question-details')}
              </Typography>
            </div>
            <div className="flex flex-row justify-between items-center gap-2">
              <Typography variant="subtitle2">{t('status')}</Typography>
              <StatusField status={entity.status} isActive={entity.isActive} />
            </div>
            <Delimiter className="my-2" />
          </div>
          <div>
            <div className="flex flex-row justify-between items-center gap-2">
              <Typography variant="subtitle2">{t('category')}</Typography>
              <CategoryField category={entity.category} />
            </div>
            <Delimiter className="my-2" />
          </div>
          <div>
            <div className="flex flex-row justify-between items-center gap-2">
              <Typography variant="subtitle2">{t('importance')}</Typography>
              <ImportanceField importance={entity.importance} />
            </div>
            <Delimiter className="my-2" />
          </div>
          <div>
            <div className="flex flex-row justify-between items-center gap-2">
              <Typography variant="subtitle2">{t('created-by')}</Typography>
              <Typography variant="subtitle2" className="font-bold">
                {getFullName(entity.createdByUser, ' ')}
              </Typography>
            </div>
            <Delimiter className="my-2" />
          </div>
          <div>
            <div className="flex flex-row justify-between items-center gap-2">
              <Typography variant="subtitle2">{t('created-on')}</Typography>
              <Typography variant="subtitle2" className="font-bold">
                <DateColumn date={entity.createdAt} withTime={true} />
              </Typography>
            </div>
            <Delimiter className="my-2" />
          </div>
          <div>
            <div className="flex flex-row justify-between items-center gap-2">
              <Typography variant="subtitle2">{t('modified-by')}</Typography>
              <Typography variant="subtitle2" className="font-bold">
                {entity.updatedByUser
                  ? getFullName(entity.updatedByUser, ' ')
                  : getFullName(entity.createdByUser, ' ')}
              </Typography>
            </div>
            <Delimiter className="my-2" />
          </div>
          <div>
            <div className="flex flex-row justify-between items-center gap-2">
              <Typography variant="subtitle2">{t('last-updated')}</Typography>
              <Typography variant="subtitle2" className="font-bold">
                <DateColumn date={entity.updatedAt} withTime={true} />
              </Typography>
            </div>
            <Delimiter className="my-2" />
          </div>
          <div>
            <div className="flex flex-row justify-between items-center gap-2">
              <Typography variant="subtitle2">
                {t('used-by-questionnaires')}
              </Typography>
              <Chip
                label={entity.usedByQuestionnairesCount || 0}
                color={
                  entity.usedByQuestionnairesCount || 0 > 0
                    ? 'primary'
                    : 'default'
                }
              />
            </div>
            <Delimiter className="my-2" />
          </div>
        </div>
        {files && files.length > 0 && (
          <div>
            <Typography variant="subtitle1" className="mb-2 font-bold">
              {t('attachments')}
            </Typography>
            <FileManager readonly={true} files={files} />
          </div>
        )}
      </div>
      {displayEditForm && (
        <QuestionForm
          title={editFormTranslation.t('edit-question')}
          question={entity}
          onFinish={onEditFinished}
          refetchQueries={[WttpQuestionsDocument]}
          onClose={onCloseEditForm}
        />
      )}
    </PageModal>
  );
};
